import Phaser from 'phaser';
import { UI_PADDING } from '../constants';

/**
 * UIScene
 * ───────
 * A scene rendered in parallel on top of GameScene.
 * Contains the HUD: player title, health bar, gold counter, and a
 * minimap placeholder.  All elements are pinned to the camera (fixed).
 */
export class UIScene extends Phaser.Scene {
  constructor() {
    super({ key: 'UIScene' });
  }

  create() {
    // Let touch events pass through to GameScene underneath
    this.input.setTopOnly(false);

    const { width, height } = this.scale;

    // ── Player title ──────────────────────────────────────────
    this.titleText = this.add
      .text(UI_PADDING, UI_PADDING, 'Peasant', {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setScrollFactor(0)
      .setDepth(1000);

    // ── Health bar ────────────────────────────────────────────
    const barX = UI_PADDING;
    const barY = UI_PADDING + 28;
    const barWidth = 160;
    const barHeight = 14;

    this.healthBg = this.add.graphics().setScrollFactor(0).setDepth(1000);
    this.healthBg.fillStyle(0x222222, 0.8);
    this.healthBg.fillRoundedRect(barX, barY, barWidth, barHeight, 4);

    this.healthBar = this.add.graphics().setScrollFactor(0).setDepth(1001);
    this.drawHealthBar(barX, barY, barWidth, barHeight, 1.0);

    // ── Gold counter ──────────────────────────────────────────
    this.goldText = this.add
      .text(UI_PADDING, barY + barHeight + 8, 'Gold: 0', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#ffd700',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setScrollFactor(0)
      .setDepth(1000);

    // ── Minimap placeholder ───────────────────────────────────
    const mmSize = 120;
    this.minimapBg = this.add.graphics().setScrollFactor(0).setDepth(1000);
    this.minimapBg.fillStyle(0x111122, 0.7);
    this.minimapBg.fillRoundedRect(width - mmSize - UI_PADDING, UI_PADDING, mmSize, mmSize, 6);
    this.minimapBg.lineStyle(2, 0x555566, 0.8);
    this.minimapBg.strokeRoundedRect(width - mmSize - UI_PADDING, UI_PADDING, mmSize, mmSize, 6);

    this.minimapLabel = this.add
      .text(width - mmSize / 2 - UI_PADDING, UI_PADDING + mmSize / 2, 'MAP', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#555566',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1001);

    // ── FPS counter (shown when debug overlay is active) ──────
    this.fpsText = this.add
      .text(UI_PADDING, height - UI_PADDING - 44, 'FPS: --', {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: '#00ff00',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setScrollFactor(0)
      .setDepth(1000)
      .setVisible(false);

    this.registry.events.on('changedata-showDebug', (_parent, value) => {
      this.fpsText.setVisible(value);
    });

    // ── Controls hint ─────────────────────────────────────────
    this.controlsText = this.add
      .text(UI_PADDING, height - UI_PADDING - 20, 'WASD: Move  |  I: Inventory  |  B: Debug', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#aaaaaa',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setScrollFactor(0)
      .setDepth(1000);

    // Reposition on resize
    this.scale.on('resize', this.handleResize, this);
  }

  drawHealthBar(x, y, w, h, pct) {
    this.healthBar.clear();
    const fillColor = pct > 0.5 ? 0x44cc44 : pct > 0.25 ? 0xcccc44 : 0xcc4444;
    this.healthBar.fillStyle(fillColor, 1);
    this.healthBar.fillRoundedRect(x, y, w * pct, h, 4);
  }

  update() {
    if (this.fpsText.visible) {
      const fps = Math.round(this.game.loop.actualFps);
      this.fpsText.setText(`FPS: ${fps}`);
    }
  }

  handleResize(gameSize) {
    const { width, height } = gameSize;
    const mmSize = 120;

    // Reposition minimap
    this.minimapBg.clear();
    this.minimapBg.fillStyle(0x111122, 0.7);
    this.minimapBg.fillRoundedRect(width - mmSize - UI_PADDING, UI_PADDING, mmSize, mmSize, 6);
    this.minimapBg.lineStyle(2, 0x555566, 0.8);
    this.minimapBg.strokeRoundedRect(width - mmSize - UI_PADDING, UI_PADDING, mmSize, mmSize, 6);

    this.minimapLabel.setPosition(width - mmSize / 2 - UI_PADDING, UI_PADDING + mmSize / 2);

    // Reposition FPS counter & controls hint
    this.fpsText.setPosition(UI_PADDING, height - UI_PADDING - 44);
    this.controlsText.setPosition(UI_PADDING, height - UI_PADDING - 20);
  }
}
